import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { api, type PerfilNome, type UsuarioDetalhe } from '../dados/api.js'
import { EscolhaObras } from './EscolhaObras.js'

/// Obras de uma pessoa, com edição.
///
/// Para quem não tem perfil de visão total, é isto que decide o que ela enxerga.
/// Quem entra pelo grupo do Entra ID nasce sem obra nenhuma — é aqui que um
/// administrador resolve.

const VEEM_TUDO: PerfilNome[] = ['SUPERADMIN', 'ADMIN', 'ENGENHEIRO_SEGURANCA', 'GERENTE_CONTRATO']

export function ObrasUsuario({ usuario }: { usuario: UsuarioDetalhe }) {
  const cliente = useQueryClient()
  const atuais = usuario.obras.map((o) => o.id)
  const [editando, setEditando] = useState(false)
  const [escolhidas, setEscolhidas] = useState<string[]>(atuais)
  const [motivo, setMotivo] = useState('')

  const salvar = useMutation({
    mutationFn: () => api.adminDefinirObras(usuario.id, escolhidas, motivo.trim() || undefined),
    onSuccess: () => {
      setEditando(false)
      setMotivo('')
      void cliente.invalidateQueries({ queryKey: ['admin'] })
    },
  })

  const veTudo = usuario.perfis.some((p) => VEEM_TUDO.includes(p))
  const mudou = escolhidas.length !== atuais.length || escolhidas.some((id) => !atuais.includes(id))

  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Obras ({usuario.obras.length})
        </p>
        {!editando && (
          <button
            onClick={() => {
              setEscolhidas(atuais)
              setEditando(true)
            }}
            className="text-xs font-semibold text-marinho hover:underline"
          >
            Alterar obras
          </button>
        )}
      </div>

      {veTudo && (
        <p className="mb-1 text-xs text-slate-500">
          Pelo perfil, esta pessoa já enxerga todas as obras. A lista abaixo só vale se o perfil mudar.
        </p>
      )}

      {editando ? (
        <div className="space-y-2">
          <EscolhaObras selecionadas={escolhidas} aoMudar={setEscolhidas} conhecidas={usuario.obras} />
          {mudou && (
            <input
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Motivo (fica no registro)"
              className="campo"
            />
          )}
          <div className="flex gap-2">
            <button
              onClick={() => salvar.mutate()}
              disabled={!mudou || salvar.isPending}
              className="botao-primario !min-h-0 flex-1 !py-1.5 text-sm"
            >
              {salvar.isPending ? 'Salvando…' : 'Salvar obras'}
            </button>
            <button
              onClick={() => {
                setEditando(false)
                setEscolhidas(atuais)
                setMotivo('')
                salvar.reset()
              }}
              className="botao-neutro !min-h-0 !py-1.5 text-sm"
            >
              Cancelar
            </button>
          </div>
          {salvar.error && (
            <p className="rounded bg-alta/10 px-3 py-2 text-sm text-alta" role="alert">
              {(salvar.error as Error).message}
            </p>
          )}
        </div>
      ) : usuario.obras.length === 0 ? (
        <p className="text-slate-500">
          {veTudo ? 'Nenhuma obra vinculada.' : 'Sem vínculo com obra. Esta pessoa entra, mas não vê nada.'}
        </p>
      ) : (
        <ul className="flex flex-wrap gap-1">
          {usuario.obras.map((o) => (
            <li key={o.id} className="etiqueta bg-white text-slate-700" title={o.fonte ?? undefined}>
              {o.codigo} · {o.nome}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
